import { styled, type Theme } from "@material-ui/core";
import { type ComponentProps, createContext, useContext } from "react";
import {
  INVOICE_CARD_BORDER_WIDTH,
  INVOICE_CARD_COLORS,
  INVOICE_CARD_RADIUS,
  INVOICE_CARD_SHADOW,
} from "./_lib/tokens";
import { type InvoiceCardLayout, useCardLayout } from "./_lib/use-card-layout";

const LayoutContext = createContext<InvoiceCardLayout | null>(null);

export const useInvoiceCardLayout = (): InvoiceCardLayout => {
  const layout = useContext(LayoutContext);
  if (!layout) {
    throw new Error(
      "useInvoiceCardLayout must be used inside an InvoiceCardShell",
    );
  }
  return layout;
};

const ShellRoot = styled("div")<Theme>(({ theme }) => ({
  width: "100%",
  // A single `minmax(0, 1fr)` column: its fixed 0 minimum is what stops the
  // line-item table's `min-width` propagating out as this card's min-content
  // width, which would otherwise force every flex/grid ancestor — up to the
  // patient page's own content column — wider than the viewport.
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  backgroundColor: theme.palette.background.paper,
  border: `${INVOICE_CARD_BORDER_WIDTH}px solid ${INVOICE_CARD_COLORS.border}`,
  borderRadius: INVOICE_CARD_RADIUS.card,
  boxShadow: INVOICE_CARD_SHADOW,
  overflow: "hidden",
}));

export const InvoiceCardShell: FCwC<
  Omit<ComponentProps<typeof ShellRoot>, "ref">
> = ({ children, ...restProps }) => {
  const { ref, layout } = useCardLayout();

  return (
    <ShellRoot ref={ref} {...restProps}>
      <LayoutContext.Provider value={layout}>{children}</LayoutContext.Provider>
    </ShellRoot>
  );
};

export type InvoiceCardSectionTone = "surface" | "alt" | "tint";

const SectionRoot = styled("div")<
  Theme,
  {
    "data-tone": InvoiceCardSectionTone;
    "data-pad-x": number;
    "data-pad-top": number;
    "data-pad-bottom": number;
    "data-divider": boolean;
  }
>(({ theme, ...props }) => ({
  backgroundColor: {
    surface: theme.palette.background.paper,
    alt: INVOICE_CARD_COLORS.altSurface,
    tint: INVOICE_CARD_COLORS.coveredTint,
  }[props["data-tone"]],
  padding: `${props["data-pad-top"]}px ${props["data-pad-x"]}px ${props["data-pad-bottom"]}px`,
  borderTop: props["data-divider"]
    ? `1px solid ${INVOICE_CARD_COLORS.divider}`
    : undefined,
  "&:first-child": {
    borderTop: "none",
  },
}));

/**
 * Owns the horizontal padding so no section repeats it; vertical padding varies
 * per band and comes in as props.
 */
export const InvoiceCardSection: FCwC<
  {
    tone?: InvoiceCardSectionTone;
    paddingTop?: number;
    paddingBottom?: number;
    divider?: boolean;
    /** For full-bleed bands (the line-item grid) whose own cells carry the lead padding. */
    disablePadX?: boolean;
  } & Omit<
    ComponentProps<typeof SectionRoot>,
    | "data-tone"
    | "data-pad-x"
    | "data-pad-top"
    | "data-pad-bottom"
    | "data-divider"
  >
> = ({
  tone = "surface",
  paddingTop = 16,
  paddingBottom = 16,
  divider = true,
  disablePadX = false,
  children,
  ...restProps
}) => {
  const { padX } = useInvoiceCardLayout();

  return (
    <SectionRoot
      data-tone={tone}
      data-pad-x={disablePadX ? 0 : padX}
      data-pad-top={paddingTop}
      data-pad-bottom={paddingBottom}
      data-divider={divider}
      {...restProps}
    >
      {children}
    </SectionRoot>
  );
};
